import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './PlanCard.css';

const PlanCard = ({ plan, readers, onStartPlan, getPlanProgress }) => {
  const { preferences } = useAuth();
  const competitiveMode = Boolean(preferences?.competitiveMode);
  const [selectedReader, setSelectedReader] = useState('');
  const [showReaderSelect, setShowReaderSelect] = useState(false);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'complete_bible':
        return '📖';
      case 'old_testament':
        return '📜';
      case 'new_testament':
        return '✝️';
      case 'custom':
        return '📋';
      default:
        return '📚';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'complete_bible':
        return 'Complete Bible';
      case 'old_testament':
        return 'Old Testament';
      case 'new_testament':
        return 'New Testament';
      case 'custom':
        return 'Custom plan';
      default:
        return 'Plan';
    }
  };

  const getEstimatedDaysText = (days) => {
    if (!days) return 'Flexible duration';
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    if (days < 30) return `${Math.round(days / 7)} weeks`;
    if (days < 365) return `${Math.round(days / 30)} months`;
    return `${Math.round(days / 365)} years`;
  };

  const handleStartPlan = () => {
    if (readers.length === 1) {
      onStartPlan(plan, readers[0]);
    } else {
      setShowReaderSelect(true);
    }
  };

  const handleReaderSelect = (readerId) => {
    const reader = readers.find(r => r.id === readerId);
    if (reader) {
      onStartPlan(plan, reader);
      setSelectedReader('');
      setShowReaderSelect(false);
    }
  };

  const getActiveReaders = () => {
    return readers.filter(reader => {
      const progress = getPlanProgress(reader.id, plan.id);
      return progress && progress.isActive;
    });
  };

  const activeReaders = getActiveReaders();

  return (
    <div className="plan-card">
      <div className="plan-header">
        <div className="plan-icon">
          {getCategoryIcon(plan.category)}
        </div>
        <div className="plan-info">
          <h3 className="plan-name">{plan.name}</h3>
          <span className="plan-category">{getCategoryLabel(plan.category)}</span>
        </div>
        {plan.isDefault && (
          <div className="default-badge">Default</div>
        )}
      </div>

      <div className="plan-description">
        {plan.description || 'A structured plan to guide your Bible study journey.'}
      </div>

      <div className="plan-stats">
        <div className="stat">
          <span className="stat-label">Chapters</span>
          <span className="stat-value">{plan.chapterCount || 'N/A'}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Duration</span>
          <span className="stat-value">{getEstimatedDaysText(plan.estimatedDays)}</span>
        </div>
        {competitiveMode && (
          <div className="stat">
            <span className="stat-label">Points</span>
            <span className="stat-value">{plan.pointsReward || 0}</span>
          </div>
        )}
      </div>

      {activeReaders.length > 0 && (
        <div className="active-readers">
          <h4>Active readers</h4>
          <div className="reader-list">
            {activeReaders.map(reader => {
              const progress = getPlanProgress(reader.id, plan.id);
              const percentage = progress ? Math.round((progress.chaptersRead / progress.totalChapters) * 100) : 0;
              
              return (
                <div key={reader.id} className="active-reader">
                  <span className="reader-name">{reader.name}</span>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="plan-actions">
        {readers.length === 0 ? (
          <button className="start-plan-button disabled" disabled>
            No reader yet
          </button>
        ) : (
          <>
            {showReaderSelect ? (
              <div className="reader-select">
                <label htmlFor="reader-select">Choose a reader</label>
                <select
                  id="reader-select"
                  value={selectedReader}
                  onChange={(e) => setSelectedReader(e.target.value)}
                >
                  <option value="">Choose a reader...</option>
                  {readers.map(reader => {
                    const progress = getPlanProgress(reader.id, plan.id);
                    const isActive = progress && progress.isActive;
                    
                    return (
                      <option 
                        key={reader.id} 
                        value={reader.id}
                        disabled={isActive}
                      >
                        {reader.name} {isActive ? '(already active)' : ''}
                      </option>
                    );
                  })}
                </select>
                <div className="reader-select-actions">
                  <button
                    className="confirm-button"
                    onClick={() => handleReaderSelect(selectedReader)}
                    disabled={!selectedReader}
                  >
                    Start plan
                  </button>
                  <button
                    className="cancel-button"
                    onClick={() => {
                      setShowReaderSelect(false);
                      setSelectedReader('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button 
                className="start-plan-button"
                onClick={handleStartPlan}
              >
                {readers.length === 1 ? 'Start plan' : 'Start plan for a reader'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlanCard;
